/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { Editor } from '@monaco-editor/react';
import { Code, AlertCircle } from 'lucide-react';
import * as monaco from 'monaco-editor';
import babelPlugin from 'prettier/plugins/babel';
import estreePlugin from 'prettier/plugins/estree';
import tsPlugin from 'prettier/plugins/typescript';
import * as prettier from 'prettier/standalone';
import { useMemo, useRef, forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { toast } from '../ui/use-toast';

import { AstParser } from './managers/AstParser';
import { DecorationManager } from './managers/DecorationManager';
import { MetadataManager } from './managers/MetadataManager';
import { StepConverter } from './managers/StepConverter';
// Bundle the healLib.d.ts file using vite raw import
import fakerDts from './types/faker.d.ts?raw';
import formanceDts from './types/formance.d.ts?raw';
import healLibDts from './types/healLib.d.ts?raw';
import otpauthDts from './types/otpauth.d.ts?raw';
import playwrightDts from './types/playwright.d.ts?raw';

import { Step as UIStep } from '@/pages/projects/LiveAgentTypes';

// Refresh the editor 50ms after the last change to prevent too many re-renders
// while the user is typing
const HEAL_EDITOR_REFRESH_INTERVAL = 50;

interface LiveAgentEditorProps {
    steps: UIStep[];
    height?: string;
    executeStep?: (step: UIStep, index: number, parentIndex?: number) => void;
    dispatch: (action: any) => void;
    queue?: { index: number; parentIndex?: number }[];
    isDisabled?: boolean;
    hidePlaywright?: boolean;
    variables: { [key: string]: string };
    highlightSelectors?: (
        selectors: { [key: string]: { selector: string; unique: boolean } } | null,
        force?: boolean,
    ) => void;
    className?: string;
    readOnly?: boolean;
}

export interface LiveAgentEditorRef {
    getCurrentSteps: () => UIStep[];
    isSyntaxValid: () => boolean;
}

export const LiveAgentEditor = forwardRef<LiveAgentEditorRef, LiveAgentEditorProps>(
    (
        {
            steps,
            height = '100%',
            executeStep,
            dispatch,
            queue,
            isDisabled,
            hidePlaywright,
            variables,
            highlightSelectors,
            className = '',
            readOnly = false,
        },
        ref,
    ) => {
        const editorRef = useRef<monaco.editor.IStandaloneCodeEditor>();
        const astParser = useMemo(() => new AstParser(), []);
        const metadataManager = useMemo(() => new MetadataManager(), []);
        const decorationManagerRef = useRef<DecorationManager>();
        const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
        const isDisabledRef = useRef(isDisabled);
        const [syntaxError, setSyntaxError] = useState<{
            hasError: boolean;
            messages: string[];
        }>({ hasError: false, messages: [] });
        const initCode = useMemo(() => {
            const code = StepConverter.stepsToCode(steps);
            if (!code) {
                return '// Write your code here';
            }
            return code;
        }, [steps]);
        const pendingExecutionRef = useRef<{ step: UIStep; index: number } | null>(null);
        const hasOneRunningStepOrOneInQueueRef = useRef(false);
        const hidePlaywrightRef = useRef(hidePlaywright);
        const variablesRef = useRef(variables);

        // Update the ref whenever isDisabled changes. Needed to prevent strange values of isDisabled
        // breaking the decoration manager. The cause is that we use these variables in monaco editor lifecycle
        // hooks.
        useEffect(() => {
            isDisabledRef.current = isDisabled;
        }, [isDisabled]);

        useEffect(() => {
            hidePlaywrightRef.current = hidePlaywright;
        }, [hidePlaywright]);

        useEffect(() => {
            variablesRef.current = variables;
        }, [variables]);

        // Method to get current steps (to be called externally)
        // We only update the steps in LiveAgentSession when the editor is unmounted (switching back to list view)
        // or when we execute a step
        // We use this method as we need to update the steps before unmounting
        const getCurrentSteps = () => {
            if (!editorRef.current) return steps;

            const c = editorRef.current.getValue();
            return StepConverter.codeToSteps(c, editorRef.current, astParser, metadataManager);
        };

        // Method to check if the syntax is valid using the AstParser
        const isSyntaxValid = () => {
            const c = editorRef.current?.getValue() ?? '';
            const { isValid } = astParser.validateSyntax(c);
            return isValid;
        };

        // Expose methods to parent component via ref
        useImperativeHandle(
            ref,
            () => ({
                getCurrentSteps,
                isSyntaxValid,
            }),
            [getCurrentSteps],
        );

        // If steps, hidePlaywright or isDisabled change, we might need to update the status from the parent
        useEffect(() => {
            for (let i = 0; i < steps.length; ++i) {
                const step = steps[i];
                const queued = queue?.some((q) => q.index === i);
                metadataManager.updateStatus(step, queued ?? false);
            }

            hasOneRunningStepOrOneInQueueRef.current = steps.some((step) => step.running) || (queue?.length ?? 0) > 0;
            const variablePositions = variablesRef.current
                ? astParser.findVariablePositions(editorRef.current?.getValue() ?? '', variablesRef.current)
                : new Map();

            // Update the decorations
            decorationManagerRef.current?.clearDecorations();
            decorationManagerRef.current?.createDecorations(
                metadataManager.getMetadata(),
                isDisabled ?? false,
                hasOneRunningStepOrOneInQueueRef.current,
                hidePlaywrightRef.current ?? false,
                variablePositions,
            );
        }, [steps, isDisabled, hidePlaywright, variables]);

        // Triggered when the editor changes, updates the metadata
        const handleEditorChange = () => {
            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Set a new timeout
            timeoutRef.current = setTimeout(() => {
                try {
                    const c = editorRef.current?.getValue() ?? '';

                    // Make sure the syntax is correct before parsing
                    const { isValid, errors } = astParser.validateSyntax(c);

                    // Update syntax error state
                    setSyntaxError({
                        hasError: !isValid,
                        messages: errors,
                    });

                    if (!isValid) {
                        return;
                    }

                    // Parse the code
                    const fc = astParser.parseCode(c);
                    const variablePositions = variablesRef.current
                        ? astParser.findVariablePositions(c, variablesRef.current)
                        : new Map();

                    // Update the metadata
                    metadataManager.updateFromAst(fc, steps, false, decorationManagerRef.current);

                    // Update the decorations
                    decorationManagerRef.current?.clearDecorations();
                    decorationManagerRef.current?.createDecorations(
                        metadataManager.getMetadata(),
                        isDisabledRef.current ?? false,
                        hasOneRunningStepOrOneInQueueRef.current,
                        hidePlaywrightRef.current ?? false,
                        variablePositions,
                    );
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error('Error updating metadata', e);
                }
            }, HEAL_EDITOR_REFRESH_INTERVAL);
        };

        const registerPrettier = (monacoInstance: typeof monaco) => {
            const makeProvider = (
                lang: 'javascript' | 'typescript',
                parser: 'babel' | 'typescript',
                plugins: any[],
            ) => ({
                provideDocumentFormattingEdits: async (model: monaco.editor.ITextModel) => {
                    const formatted = await prettier.format(model.getValue(), {
                        parser,
                        plugins,
                        semi: true,
                        singleQuote: true,
                        tabWidth: 4,
                        trailingComma: 'none',
                        printWidth: 80,
                    });

                    return [
                        {
                            range: model.getFullModelRange(),
                            text: formatted,
                        },
                    ];
                },
            });

            monacoInstance.languages.registerDocumentFormattingEditProvider(
                'javascript',
                makeProvider('javascript', 'babel', [babelPlugin, estreePlugin]),
            );

            monacoInstance.languages.registerDocumentFormattingEditProvider(
                'typescript',
                makeProvider('typescript', 'typescript', [tsPlugin, estreePlugin]),
            );
        };

        const handleEditorBeforeMount = (monacoInstance: typeof monaco) => {
            monacoInstance.languages.typescript.javascriptDefaults.addExtraLib(
                healLibDts,
                'inmemory://model/healLib.d.ts',
            );

            monacoInstance.languages.typescript.javascriptDefaults.addExtraLib(
                playwrightDts,
                'inmemory://model/playwright.d.ts',
            );

            monacoInstance.languages.typescript.javascriptDefaults.addExtraLib(fakerDts, 'inmemory://model/faker.d.ts');

            monacoInstance.languages.typescript.javascriptDefaults.addExtraLib(
                otpauthDts,
                'inmemory://model/otpauth.d.ts',
            );

            monacoInstance.languages.typescript.javascriptDefaults.addExtraLib(
                formanceDts,
                'inmemory://model/formance.d.ts',
            );

            registerPrettier(monacoInstance);
        };

        const executeStepLocal = (lineNumber: number) => {
            const newSteps = getCurrentSteps();

            // Get the metadata for the current line
            const metadata = metadataManager.getMetadataByLineNumber(lineNumber);

            // No check on the "executed" flag as we want to allow running the same step multiple times
            if (!metadata || metadata.running) return;

            const stepIndex = newSteps.findIndex((step) => step.id === metadata.key);
            if (stepIndex === -1) {
                toast({
                    title: 'ERROR: Step not found',
                    variant: 'destructive',
                });
                return;
            }

            // Save the pending execution
            pendingExecutionRef.current = {
                step: newSteps[stepIndex],
                index: stepIndex,
            };

            // Update state
            dispatch({
                type: 'set',
                payload: newSteps,
            });
        };

        const handleEditorMouseDown = (e: monaco.editor.IEditorMouseEvent) => {
            if (readOnly) return;
            const t = e.target;
            if (t.type === monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN && t.position?.lineNumber) {
                if (hasOneRunningStepOrOneInQueueRef.current) return;

                if (!isSyntaxValid()) {
                    toast({
                        title: 'Syntax error in the code editor, please fix it before running a step',
                        variant: 'destructive',
                    });
                    return;
                }

                executeStepLocal(t.position?.lineNumber);
            }
        };

        // This useEffect handles the execution after state update
        useEffect(() => {
            if (pendingExecutionRef.current && executeStep) {
                const { step, index } = pendingExecutionRef.current;
                executeStep(step, index);
                pendingExecutionRef.current = null;
            }
        }, [steps, executeStep]);

        const handleFormatCode = () => {
            if (editorRef.current) {
                editorRef.current.getAction('editor.action.formatDocument')?.run();
            }
        };

        const handleEditorKeyDown = (e: monaco.IKeyboardEvent) => {
            if (readOnly) return;
            const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
            const isCtrlEnter =
                (!isMac && e.ctrlKey && e.code === 'Enter') || (isMac && e.metaKey && e.code === 'Enter');

            // There is a global handler on Shift in LiveAgentSession, we do not need to handle it here
            if (isCtrlEnter) {
                e.preventDefault();
                e.stopPropagation();

                if (hasOneRunningStepOrOneInQueueRef.current) return;

                if (!isSyntaxValid()) {
                    toast({
                        title: 'Syntax error in the code editor, please fix it before running a step',
                        variant: 'destructive',
                    });
                    return;
                }

                const position = editorRef.current?.getPosition();
                if (!position) return;

                executeStepLocal(position.lineNumber);
            }
        };

        const showSelector = () => {
            if (!editorRef.current) return;

            const selection = editorRef.current.getSelection();
            if (!selection) return;

            const model = editorRef.current.getModel();
            if (!model) return;

            const selectedText = model.getValueInRange(selection);
            if (!selectedText) return;

            const code = model.getValue();
            // Analyze the selected text using our new method
            const analysis = astParser.analyzeHighlightedText(code, selectedText);

            if (analysis.type === 'variable-method' || analysis.type === 'function') {
                if (
                    analysis.functionPart &&
                    (analysis.functionPart.startsWith('getBy') || analysis.functionPart === 'locator')
                ) {
                    // Highlight the selector
                    highlightSelectors?.({ playwrightCode: { selector: analysis.functionPart || '', unique: true } });
                    return;
                }
            }

            highlightSelectors?.(null);

            toast({
                title: 'Not Supported',
                description: `The selector ${selectedText} is not supported.`,
                variant: 'destructive',
            });
        };

        const handleEditorMount = (editor: monaco.editor.IStandaloneCodeEditor) => {
            editorRef.current = editor;

            // Check the syntax of the initial code
            const { isValid, errors } = astParser.validateSyntax(initCode);

            // Update syntax error state
            setSyntaxError({
                hasError: !isValid,
                messages: errors,
            });

            // Parse the code
            const functionCalls = astParser.parseCode(initCode);
            const variablePositions = variablesRef.current
                ? astParser.findVariablePositions(initCode, variablesRef.current)
                : new Map();

            // Init metadata with input steps
            metadataManager.updateFromAst(functionCalls, steps, true, decorationManagerRef.current);

            // Setup decoration manager
            decorationManagerRef.current = new DecorationManager(editor, metadataManager);

            // Set up decorations
            decorationManagerRef.current.createDecorations(
                metadataManager.getMetadata(),
                isDisabled ?? false,
                hasOneRunningStepOrOneInQueueRef.current,
                hidePlaywrightRef.current ?? false,
                variablePositions,
            );

            // Set up change handler
            editor.onDidChangeModelContent(handleEditorChange);

            // Set up clipboard events
            editor.onKeyDown((e) => decorationManagerRef.current?.onKeyDown(e));
            editor.onDidPaste((e) => decorationManagerRef.current?.onDidPaste(e));

            // Handle custom keyboard shortcuts
            editor.onKeyDown(handleEditorKeyDown);

            // Handle play button click
            // 3) hook clicks in the glyph margin
            editor.onMouseDown(handleEditorMouseDown);

            // Register custom actions in context menu
            editor.addAction({
                id: 'highlight-selector',
                label: '🌟 Highlight selector',
                contextMenuGroupId: '0_custom_groups', // or '1_modification', etc.
                contextMenuOrder: 0, // float to place it between items
                keybindings: [
                    monaco.KeyMod.CtrlCmd | monaco.KeyCode.Period, // Ctrl+. or Cmd+.
                ],
                run: (ed) => {
                    showSelector();
                },
            });
        };

        return (
            <div className={className}>
                <div className="relative h-full">
                    {syntaxError.hasError && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        className="absolute bottom-6 right-14 z-10
                                                  bg-red-800 hover:bg-red-700 rounded p-1.5"
                                    >
                                        <AlertCircle size={16} className="text-white" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-md">
                                    <div>
                                        <p className="font-semibold">Syntax Error:</p>
                                        {syntaxError.messages.map((msg, i) => (
                                            <p key={i} className="text-xs">
                                                {msg}
                                            </p>
                                        ))}
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                    <button
                        onClick={handleFormatCode}
                        className="absolute bottom-6 right-6 z-10 bg-slate-700 hover:bg-slate-600 rounded p-1.5"
                        title="Format code"
                    >
                        <Code size={16} className="text-white" />
                    </button>
                    <Editor
                        height={height}
                        defaultLanguage="javascript"
                        beforeMount={handleEditorBeforeMount}
                        onMount={handleEditorMount}
                        value={initCode}
                        options={{
                            minimap: { enabled: false },
                            fontSize: 12,
                            lineNumbers: 'on',
                            roundedSelection: false,
                            readOnly,
                            automaticLayout: true,
                            // Small icons to the left of the line numbers
                            glyphMargin: !readOnly,
                            // This is needed to ensure the hover is above the editor
                            fixedOverflowWidgets: true,
                        }}
                        theme="vs-dark"
                    />
                </div>
            </div>
        );
    },
);
